import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Share2, Sparkles, Users, Clock } from 'lucide-react';
import ProductModal from '@/components/ProductModal';
import ProductCardSkeleton from '@/components/ProductCardSkeleton';
import { SoftTechBanner } from '@/components/catalog/SoftTechBanner';
import { trackShare } from '@/lib/analytics';
import { useLanguage } from '@/context/LanguageContext';
import { t, tx } from '@/translations/translations';
import { useProducts } from '@/hooks/useProducts';

const ProductCatalog = () => {
  const { language } = useLanguage();
  const { products, loading } = useProducts();
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Always render the live (current-language) version of the opened product
  const liveSelectedProduct = selectedProduct
    ? (products.find(p => p.id === selectedProduct.id) || selectedProduct)
    : null;
  const [filter, setFilter] = useState('todos');
  const [filterGroups, setFilterGroups] = useState([]);

  useEffect(() => {
    let timer;
    const loadGroups = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/filter-groups?_t=${Date.now()}`, { cache: 'no-store' })
        .then(r => (r.ok ? r.json() : []))
        .then(g => Array.isArray(g) && setFilterGroups(g))
        .catch(() => {});
    };
    const debounced = () => { clearTimeout(timer); timer = setTimeout(loadGroups, 500); };
    loadGroups();
    window.addEventListener('productsUpdated', debounced);
    return () => { clearTimeout(timer); window.removeEventListener('productsUpdated', debounced); };
  }, []);

  useEffect(() => {
    const handleFilter = (e) => setFilter(e.detail);
    window.addEventListener('filterMaterial', handleFilter);
    return () => window.removeEventListener('filterMaterial', handleFilter);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const productoId = params.get('producto');
    if (productoId) {
      const product = products.find(p => p.id === parseInt(productoId));
      if (product) {
        setSelectedProduct(product);
        setTimeout(() => {
          document.getElementById('productos')?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    }
  }, [products]);

  const materials = [
    { value: 'todos', label: t(language, 'products.filterAll') },
    ...(filterGroups.length > 0
      ? filterGroups.map(g => ({
          value: g.group_id,
          label: g.label?.[language] || g.label?.es || '',
        }))
      : [
          { value: 'lana', label: t(language, 'products.filterWool') },
          { value: 'algodon', label: t(language, 'products.filterCotton') },
          { value: 'bambu', label: t(language, 'products.filterBamboo') },
          { value: 'poliester', label: 'Dry-Fit' },
        ]),
  ];

  const filteredProducts = useMemo(() => {
    if (filter === 'todos') return products;
    const group = filterGroups.find(g => g.group_id === filter);
    if (group) return products.filter(p => (group.product_ids || []).includes(p.id));
    return products.filter(p => p.material === filter);
  }, [products, filter, filterGroups]);

  const isSoftTech = (p) => p.material === 'lana' && (p.style === 'Tank Top' || p.style === 'Tank Top V-Neck' || p.style === 'T-Shirt' || p.style === 'Kol Haposkim');

  return (
    <section id="productos" className="py-12 md:py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 md:mb-14">
          <p className="text-xs font-bold text-cyan-600 tracking-[0.25em] uppercase mb-4">
            {tx(language, 'Top Worldwide Fabrics', 'Top Worldwide Fabrics', 'הבדים הטובים בעולם')}
          </p>
          <h2 className="hidden md:block text-4xl md:text-5xl font-bold text-slate-900 mb-3">
            {t(language, 'products.title')}
          </h2>
          <div className="flex items-center justify-center gap-4 mt-2">
            <div className="w-12 h-px bg-slate-300" />
            <p className="text-base text-slate-400 italic">
              {tx(language, 'A Higher Tzitzit Experience', 'A Higher Tzitzit Experience', 'חוויית ציצית ברמה אחרת')}
            </p>
            <div className="w-12 h-px bg-slate-300" />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 md:mb-14">
          {materials.map((mat) => (
            <button
              key={mat.value}
              onClick={() => setFilter(mat.value)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === mat.value
                  ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400 hover:shadow-md'
              }`}
              data-testid={`filter-${mat.value}`}
            >
              {mat.label}
            </button>
          ))}
        </div>

        {/* Products */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5">
            {Array.from({ length: 10 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        ) : (<>
          {/* Soft Tech Banner — shown when Lana filter is active */}
          {filter === 'lana' && <SoftTechBanner language={language} />}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5 sm:gap-4 lg:gap-5 items-stretch">
            {filteredProducts.filter(p => !p.isPromo).map((product) => {
              const hasKids = product.sizes?.some((s) => /niño|boys/i.test(s));
              const hasAdult = product.sizes?.some((s) => /adulto|adult/i.test(s));
              const ageLabel = hasKids && hasAdult
                ? tx(language, 'Adulto y Niño', 'Adult & Kids', 'מבוגרים וילדים')
                : hasKids
                ? tx(language, 'Niño', 'Kids', 'ילדים')
                : hasAdult
                ? tx(language, 'Adulto', 'Adult', 'מבוגרים')
                : null;
              return (
              <div
                key={product.id}
                onClick={() => setSelectedProduct(product)}
                className="group relative cursor-pointer rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-white hover:ring-cyan-400/60 hover:shadow-[0_20px_50px_-15px_rgba(8,145,178,0.25)] transition-all duration-400 flex flex-col hover:-translate-y-1"
                data-testid={`product-card-${product.id}`}
              >
                {/* Image area */}
                <div className="relative aspect-[4/5] overflow-hidden bg-white">
                  <div className="absolute inset-2 sm:inset-3 rounded-xl overflow-hidden bg-white">
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover object-center group-hover:scale-[1.06] transition-transform duration-700 ease-out"
                    />
                  </div>

                  {/* Stock badge — top-right */}
                  {product.comingSoon ? (
                    <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white/95 backdrop-blur-sm text-amber-700 text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm ring-1 ring-amber-200">
                      {tx(language, 'Prox.', 'Soon', 'בקרוב')}
                    </span>
                  ) : product.inStock ? (
                    <span className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-emerald-700 text-[9px] sm:text-[10px] font-semibold pl-1.5 pr-2 py-0.5 rounded-full shadow-sm ring-1 ring-emerald-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      {t(language, 'products.inStock')}
                    </span>
                  ) : (
                    <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white/95 backdrop-blur-sm text-rose-700 text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm ring-1 ring-rose-100">
                      {tx(language, 'Agotado', 'Sold Out', 'אזל מהמלאי')}
                    </span>
                  )}

                  {/* NEW badge — top-left */}
                  {product.isNew && (
                    <span
                      data-testid={`new-badge-${product.id}`}
                      className="absolute top-2 left-2 sm:top-3 sm:left-3 inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] sm:text-[10px] font-black px-2 py-0.5 rounded-full shadow-md ring-1 ring-white/30 uppercase tracking-wider"
                    >
                      <Sparkles className="w-2.5 h-2.5" />
                      New
                    </span>
                  )}

                  {/* Share button — top-left on hover */}
                  <button
                    data-testid={`share-btn-${product.id}`}
                    className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white shadow-md ring-1 ring-slate-200 flex items-center justify-center opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 hover:bg-green-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/api/p/${product.id}`;
                      const txt = `${product.name} - $${product.price.toFixed(2)} ${product.currency}\n${url}`;
                      trackShare(product.id, 'whatsapp');
                      window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, '_blank');
                    }}
                  >
                    <Share2 className="h-3.5 w-3.5 text-emerald-600" />
                  </button>
                </div>

                {/* Info — detailed specs */}
                <div className="p-2.5 sm:p-4 flex flex-col gap-2 sm:gap-2.5 flex-1">
                  <h3 className="text-[13px] sm:text-[15px] font-bold leading-snug text-slate-900 line-clamp-2 min-h-[2.4em] sm:min-h-[2.6em]">
                    {product.name}
                  </h3>

                  {/* Age + Soft Tech tags */}
                  <div className="flex items-center flex-wrap gap-1 sm:gap-1.5">
                    {ageLabel && (
                      <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 rounded-full">
                        <Users className="w-2.5 h-2.5" />
                        {ageLabel}
                      </span>
                    )}
                    {isSoftTech(product) && (
                      <span className="inline-flex items-center gap-1 bg-cyan-50 text-cyan-700 text-[8px] sm:text-[9px] font-black px-1.5 sm:px-2 py-0.5 rounded-full ring-1 ring-cyan-200 uppercase tracking-wider">
                        <Sparkles className="w-2.5 h-2.5" />
                        Soft Tech
                      </span>
                    )}
                    {(product.badges || []).map((b) => (
                      <span key={b} data-testid={`custom-badge-${product.id}`} className="inline-flex items-center bg-slate-900 text-white text-[8px] sm:text-[9px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full uppercase tracking-wider">
                        {b}
                      </span>
                    ))}
                  </div>

                  {/* Specs */}
                  <div className="flex flex-col gap-0.5 sm:gap-1 text-[10px] sm:text-[11px] leading-tight">
                    {product.fabric && (
                      <div className="flex gap-1">
                        <span className="text-slate-500 font-medium">{tx(language, 'Tela:', 'Fabric:', 'בד:')}</span>
                        <span className="text-slate-800 truncate">{product.fabric}</span>
                      </div>
                    )}
                    {product.style && (
                      <div className="flex gap-1">
                        <span className="text-slate-500 font-medium">{tx(language, 'Estilo:', 'Style:', 'סגנון:')}</span>
                        <span className="text-slate-800 truncate">{product.style}</span>
                      </div>
                    )}
                    {product.colors && product.colors.length > 0 && (
                      <div className="flex items-center gap-1.5 pt-0.5">
                        <span className="text-slate-500 font-medium">{tx(language, 'Colores:', 'Colors:', 'צבעים:')}</span>
                        <div className="flex items-center -space-x-1">
                          {product.colors.slice(0, 5).map((color) => (
                            <div
                              key={`${product.id}-${color.value}`}
                              className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full border-2 border-white shadow-sm ring-1 ring-slate-200"
                              style={{ backgroundColor: color.value }}
                              title={color.name}
                            />
                          ))}
                          {product.colors.length > 5 && (
                            <span className="relative ml-1.5 text-[9px] font-semibold text-slate-500">
                              +{product.colors.length - 5}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="flex items-end justify-between gap-2 mt-auto pt-1 sm:pt-2">
                    <div className="flex flex-col">
                      {product.priceMax && (
                        <span className="text-[9px] uppercase tracking-widest font-medium text-slate-400">
                          {tx(language, 'Desde', 'From', 'החל מ-')}
                        </span>
                      )}
                      <span className="text-lg sm:text-2xl font-black leading-none tracking-tight text-cyan-600">
                        ${product.price.toFixed(0)}
                        <span className="text-[10px] sm:text-[11px] font-bold ml-1 align-top text-cyan-600/70">{product.currency}</span>
                      </span>
                      {product.priceIls && (
                        <span className="text-[10px] font-semibold text-slate-400 mt-0.5" data-testid={`ils-price-${product.id}`}>
                          ≈ ₪{product.priceIls.toLocaleString('he-IL')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Agregar button — full width */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProduct(product);
                    }}
                    disabled={!product.inStock || product.comingSoon}
                    data-testid={`add-btn-${product.id}`}
                    className="mt-1 w-full inline-flex items-center justify-center gap-1.5 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold text-xs sm:text-sm py-2 sm:py-2.5 rounded-lg transition-colors duration-200 shadow-sm"
                  >
                    {product.comingSoon ? (
                      <>
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        {tx(language, 'Próximamente', 'Coming Soon', 'בקרוב')}
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        {tx(language, 'Agregar', 'Add', 'הוסף')}
                      </>
                    )}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </>)}

      {/* Product Modal */}
      {liveSelectedProduct && (
        <ProductModal
          product={liveSelectedProduct}
          onClose={() => setSelectedProduct(null)}
          allProducts={products}
          onSelectProduct={(p) => setSelectedProduct(p)}
        />
      )}
      </div>
    </section>
  );
};

export default ProductCatalog;
